/**
 * Cura — Shared Scroll Animation System
 * -----------------------------------------------------------------
 * Lightweight IntersectionObserver-driven entrance animations.
 * Loaded ONCE globally via theme.liquid. Do not re-implement per section.
 *
 * Sections opt in by rendering the `cura-animation-attributes` snippet
 * on their wrapper element, which outputs data-animate / data-animate-delay.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-animate]';
  var VISIBLE_CLASS = 'is-visible';
  var observer = null;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function revealImmediately(elements) {
    elements.forEach(function (el) {
      el.classList.add(VISIBLE_CLASS);
    });
  }

  function handleIntersect(entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var target = entry.target;

      // If the intersecting element is a GROUP container, reveal all its
      // animated children (carousel-safe), then stop watching the group.
      if (target.dataset.animateGroupObserved) {
        var kids = target.querySelectorAll(SELECTOR);
        kids.forEach(function (kid) {
          var delay = kid.getAttribute('data-animate-delay');
          if (delay) kid.style.setProperty('--animate-delay', delay + 'ms');
          kid.classList.add(VISIBLE_CLASS);
        });
        observer.unobserve(target);
        return;
      }

      // Otherwise it's an individual element — reveal it as before.
      var delay = target.getAttribute('data-animate-delay');
      if (delay) target.style.setProperty('--animate-delay', delay + 'ms');
      target.classList.add(VISIBLE_CLASS);
      observer.unobserve(target);
    });
  }

  function initAnimations(root) {
    var scope = root && root.querySelectorAll ? root : document;

    // Reduced motion or no IntersectionObserver: reveal everything immediately.
    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      revealImmediately(scope.querySelectorAll(SELECTOR));
      return;
    }

    if (!observer) {
      observer = new IntersectionObserver(handleIntersect, {
        threshold: 0.15,
        rootMargin: '0px 0px -10% 0px'
      });
    }

    // Find the nearest meaningful "group" container for each animated element.
    // For elements inside a scrollable/slider track, we reveal the whole group
    // together (so horizontally off-screen carousel items don't stay stuck in
    // their transform state, which would expand the track and clip cards).
    var animated = Array.prototype.slice.call(scope.querySelectorAll(SELECTOR));
    animated.forEach(function (el) {
      if (el.classList.contains(VISIBLE_CLASS) || el.dataset.animateObserved) return;
      el.dataset.animateObserved = 'true';

      var group = el.closest('[data-animate-group], .cura-rev__track, .cura-collcar__track, .cura-cg__grid, .cura-bsg__grid, .cura-fs__grid, .cura-sbc__grid, [data-collcar-track], [data-rev-track]');
      if (group) {
        // Observe the group once; reveal all its animated children together.
        if (!group.dataset.animateGroupObserved) {
          group.dataset.animateGroupObserved = 'true';
          observer.observe(group);
        }
      } else {
        // Normal element: observe it individually as before.
        observer.observe(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAnimations(document);
    });
  } else {
    initAnimations(document);
  }

  // Re-scan when Theme Editor injects or reloads a section (live preview support)
  document.addEventListener('shopify:section:load', function (event) {
    initAnimations(event.target);
  });
  document.addEventListener('cura:content-loaded', function (event) {
    initAnimations(event.detail && event.detail.container ? event.detail.container : document);
  });
})();